import { getOrderItemSalesBreakdown, getProductSalesBreakdown, getSellerSalesBreakdown, getSellerSalesSnapshot } from "@/api/seller-analytics";
import { SalesSnapshotCardProps } from "@/app/dashboard/SalesSnapshotCard/SalesSnapshotCard";
import { OrderItemSalesResponseBody, ProductSalesItem, ProductSalesResponseBody, SalesBreakdownResponseBody, SalesSnapshotResponseBody, SkuSalesResponseBody, UserRole } from "@boxem/core-api-schema";
import dayjs from "dayjs";
import { Dispatch, PropsWithChildren, createContext, useContext, useEffect, useReducer, useState } from 'react';
import { useUserContext } from "./UserContext";
import { DATE_RANGE_PRESETS, DateRangeValue, startOfToday as startOfTodayObject } from "@/utils/dateUtils";
import { LocalStorageKeys } from "@/utils/localStorageUtils";
import { logRocketError } from "@/utils/logUtils";

interface SalesBreakdownChart {
  isLoading?: boolean;
  data: SalesBreakdownResponseBody;
}

interface ProductSalesBreakdown {
  isTableLoading?: boolean;
  isDetailLoading?: boolean;
  isDetailOpen?: boolean;
  tableData: ProductSalesResponseBody;
  detailData?: {
    listing: ProductSalesItem;
    detail?: SkuSalesResponseBody
  };
}
interface OrderItemSalesBreakdown {
  isTableLoading?: boolean;
  isDetailLoading?: boolean;
  isDetailOpen?: boolean;
  tableData: OrderItemSalesResponseBody;
}
export type BreakdownTab = 'sales' | 'products' | 'order-items';
type DashboardPageVisited = { type: 'DASHBOARD_PAGE_VISITED' }
type AllSnapshotDataLoaded = { type: 'ALL_SNAPSHOT_DATA_LOADED', data: { salesData: SalesSnapshotResponseBody } }
type SnapshotIntervalChanged = { type: 'SNAPSHOT_INTERVAL_CHANGED', data: { cardIndex: number, interval: DateRangeValue } }
type SnapshotDataLoaded = { type: 'SNAPSHOT_DATA_LOADED', data: { cardIndex: number, salesData: SalesSnapshotResponseBody } }
type SnapshotCardClicked = { type: 'SNAPSHOT_CARD_CLICKED', data: { cardIndex: number } }
type BreadownIntervalChanged = { type: 'BREAKDOWN_INTERVAL_CHANGED' }
type BreakdownTabChanged = { type: 'BREAKDOWN_TAB_CHANGED', data: { tab: BreakdownTab } }
type SalesBreakdownDataLoaded = { type: 'SALES_BREAKDOWN_DATA_LOADED', data: { breakdownData: SalesBreakdownResponseBody } }
type ProductSalesBreakdownDataLoaded = { type: 'PRODUCT_SALES_BREAKDOWN_DATA_LOADED', data: { tableData: ProductSalesResponseBody } }
type ProductSalesBreakdownRowActionClicked = { type: 'PRODUCT_SALES_BREAKDOWN_ROW_ACTION_CLICKED', data: { listing: ProductSalesItem } }
type ProductSalesBreakdownDetailDaLoaded = { type: 'PRODUCT_SALES_BREAKDOWN_DETAIL_LOADED', data: { detailData: SkuSalesResponseBody } }
type ProductSalesBreakdownDetailClosed = { type: 'PRODUCT_SALES_BREAKDOWN_DETAIL_CLOSED' }
type OrderItemSalesBreakdownDataLoaded = { type: 'ORDER_ITEM_SALES_BREAKDOWN_DATA_LOADED', data: { tableData: OrderItemSalesResponseBody } }
export type DashboardAction = DashboardPageVisited | AllSnapshotDataLoaded | SnapshotIntervalChanged | SnapshotDataLoaded | SnapshotCardClicked | SalesBreakdownDataLoaded | BreadownIntervalChanged | BreakdownTabChanged | ProductSalesBreakdownDataLoaded | ProductSalesBreakdownRowActionClicked | ProductSalesBreakdownDetailDaLoaded | ProductSalesBreakdownDetailClosed | OrderItemSalesBreakdownDataLoaded

export interface DashboardState {
  activeSnapshotIndex: number;
  activeBreakdownTab: BreakdownTab;
  salesSnapshotCards: SalesSnapshotCardProps[];
  salesBreakdownChart: SalesBreakdownChart;
  productSalesBreakdown: ProductSalesBreakdown;
  orderItemSalesBreakdown: OrderItemSalesBreakdown;
}

const startOfToday = dayjs().startOf('day').toDate();
const endOfToday = dayjs().endOf('day').toDate();
const dateBeginningOfLastMonth = dayjs().subtract(1, 'month').startOf('month').startOf('day').toDate();
const dateEndOfLastMonth = dayjs().subtract(1, 'month').endOf('month').endOf('day').toDate();


const defaultSaleSnaphotIntervals: DateRangeValue[] = [
  {
    label: 'Today',
    from: startOfToday,
    to: endOfToday,
  },
  {
    label: 'Past 7 days', 
    from: dayjs().subtract(7, 'days').startOf('day').toDate(), 
    to: startOfTodayObject.subtract(1, 'day').endOf('day').toDate(),
  },
  {
    label: 'Past 30 days', 
    from: dayjs().subtract(30, 'days').startOf('day').toDate(), 
    to: startOfTodayObject.subtract(1, 'day').endOf('day').toDate(),
  },
  {
    label: 'Last month', 
    from: dateBeginningOfLastMonth, 
    to: dateEndOfLastMonth
  }]

const getInitalSnapshotIntervals = (): DateRangeValue[] => {

  const savedSnapshotIntervals = window.localStorage.getItem(LocalStorageKeys.salesSnapshotIntervals);
  if (savedSnapshotIntervals) {
    const parsedIntervals = JSON.parse(savedSnapshotIntervals);
    if (!Array.isArray(parsedIntervals)) {
      return defaultSaleSnaphotIntervals;
    }
    return parsedIntervals?.map((interval: DateRangeValue) => {
      const matchingDefaultInterval = defaultSaleSnaphotIntervals.find((defaultInterval) => defaultInterval.label === interval.label);
      const matchingDatePreset = DATE_RANGE_PRESETS.find((preset) => preset.label === interval.label);

      if (matchingDefaultInterval) {
        return matchingDefaultInterval;
      } else if (matchingDatePreset) {
        return matchingDatePreset;
      }
      else {
        return {
          label: interval.label,
          from: new Date(interval.from),
          to: interval.to ? new Date(interval.to) : undefined
        }
      }
    })
  } else {
    return defaultSaleSnaphotIntervals;
  }
}

const getInitialActiveSnapshotIndex = (): number => {
  const savedActiveSnapshotIndex = window.localStorage.getItem(LocalStorageKeys.activeSnapshotIndex);
  return savedActiveSnapshotIndex ? parseInt(savedActiveSnapshotIndex) : 0;
}

const getInitialSalesSnapshotData = (): SalesSnapshotCardProps[] => {
  return getInitalSnapshotIntervals().map((interval, i) => {
    return {
      defaultInterval: interval,
      isLoading: true,
      cardIndex: i,
      activeSnapshotIndex: getInitialActiveSnapshotIndex(),
    }
  });
}

const getInitialBreakdownTab = (): BreakdownTab => {
  const searchParams = new URLSearchParams(window.location.search);
  const activeBreakdownTab: string = searchParams.get("view");
  return activeBreakdownTab === 'products' ? 'products' : 'sales';
}


const intitialSalesBreakdownData: SalesBreakdownChart = {
  isLoading: true,
  data: []
}

const initialProductSalesBreakdownData: ProductSalesBreakdown = {
  isTableLoading: true,
  tableData: {
    products: [],
    total: 0,
  },
  isDetailLoading: false,
  isDetailOpen: false,
  detailData: undefined,
}

const initialOrderItemSalesBreakdownData: OrderItemSalesBreakdown = {
  isTableLoading: true,
  tableData: {
    orderItems: [],
    total: 0,
  },
  isDetailLoading: false,
  isDetailOpen: false,
}

export const initialDashboardState: DashboardState = {
  activeSnapshotIndex: getInitialActiveSnapshotIndex(),
  activeBreakdownTab: getInitialBreakdownTab(),
  salesSnapshotCards: getInitialSalesSnapshotData(),
  salesBreakdownChart: intitialSalesBreakdownData,
  productSalesBreakdown: initialProductSalesBreakdownData,
  orderItemSalesBreakdown: initialOrderItemSalesBreakdownData,
}

const dashboardReducer = (state: DashboardState, action: DashboardAction): DashboardState => {
  switch (action.type) {
    case 'DASHBOARD_PAGE_VISITED':
      return {
        ...state,
        salesSnapshotCards: [
          ...state.salesSnapshotCards.map((snapshot) => {
            return {
              ...snapshot,
              isLoading: true
            }
          }),
        ],
        salesBreakdownChart: {
          ...state.salesBreakdownChart,
          isLoading: state.activeBreakdownTab === 'sales',
        },
        productSalesBreakdown: {
          ...state.productSalesBreakdown,
          isTableLoading: state.activeBreakdownTab === 'products',
        }
      }
    case 'ALL_SNAPSHOT_DATA_LOADED':
      return {
        ...state,
        salesSnapshotCards: [
          ...(state.salesSnapshotCards.length ? state.salesSnapshotCards.map((snapshot, i) => {
            return {
              ...snapshot,
              data: action.data?.salesData?.[i] ? action.data.salesData[i] : snapshot.data,
              isLoading: false,
            }
          }) : state.salesSnapshotCards)
        ]
      }
    case 'SNAPSHOT_INTERVAL_CHANGED':
      return {
        ...state,
        salesSnapshotCards: [
          ...(state.salesSnapshotCards.length ? state.salesSnapshotCards.map((snapshot, i) => {
            return {
              ...snapshot,
              isLoading: action.data.cardIndex === i,
              defaultInterval: action.data.cardIndex === i ? action.data.interval : snapshot.defaultInterval,
            }
          }) : state.salesSnapshotCards)
        ]
      }
    case 'SNAPSHOT_DATA_LOADED':
      return {
        ...state,
        salesSnapshotCards: [
          ...state.salesSnapshotCards.map((snapshot, i) => {
            return {
              ...snapshot,
              data: action.data.cardIndex === i && action.data?.salesData?.[0] ? action.data.salesData[0] : snapshot.data,
              isLoading: false,
            }
          })
        ],
      }
    case 'SNAPSHOT_CARD_CLICKED':
      return {
        ...state,
        activeSnapshotIndex: action.data.cardIndex,
        salesBreakdownChart: {
          ...state.salesBreakdownChart,
          isLoading: state.activeBreakdownTab === 'sales',
        },
        productSalesBreakdown: {
          ...state.productSalesBreakdown,
          isTableLoading: state.activeBreakdownTab === 'products',
        }
      }
    case 'BREAKDOWN_INTERVAL_CHANGED':
      return {
        ...state,
        salesBreakdownChart: {
          ...state.salesBreakdownChart,
          isLoading: state.activeBreakdownTab === 'sales',
        },
        productSalesBreakdown: {
          ...state.productSalesBreakdown,
          isTableLoading: state.activeBreakdownTab === 'products',
        }
      }
    case 'BREAKDOWN_TAB_CHANGED':
      return {
        ...state,
        activeBreakdownTab: action.data.tab,
        salesBreakdownChart: {
          ...state.salesBreakdownChart,
          isLoading: action.data.tab === 'sales',
        },
        productSalesBreakdown: {
          ...state.productSalesBreakdown,
          isTableLoading: action.data.tab === 'products',
        }
      }
    case 'SALES_BREAKDOWN_DATA_LOADED':
      return {
        ...state,
        salesBreakdownChart: {
          ...state.salesBreakdownChart,
          data: action.data.breakdownData,
          isLoading: false,
        }
      }
    case 'PRODUCT_SALES_BREAKDOWN_DATA_LOADED':
      return {
        ...state,
        productSalesBreakdown: {
          ...state.productSalesBreakdown,
          tableData: action.data.tableData,
          isTableLoading: false,
        }
      }
    case 'PRODUCT_SALES_BREAKDOWN_ROW_ACTION_CLICKED':
      return {
        ...state,
        productSalesBreakdown: {
          ...state.productSalesBreakdown,
          isDetailLoading: true,
          isDetailOpen: true,
          detailData: {
            listing: action.data.listing,
            detail: undefined,
          }
        }
      }
    case 'PRODUCT_SALES_BREAKDOWN_DETAIL_LOADED':
      return {
        ...state,
        productSalesBreakdown: {
          ...state.productSalesBreakdown,
          detailData: {
            ...state.productSalesBreakdown.detailData,
            detail: action.data.detailData,
          },
          isDetailLoading: false,
        }
      }
    case 'PRODUCT_SALES_BREAKDOWN_DETAIL_CLOSED':
      return {
        ...state,
        productSalesBreakdown: {
          ...state.productSalesBreakdown,
          isDetailLoading: false,
          isDetailOpen: false,
          detailData: undefined,
        }
      }
    case 'ORDER_ITEM_SALES_BREAKDOWN_DATA_LOADED':
      return {
        ...state,
        orderItemSalesBreakdown: {
          ...state.orderItemSalesBreakdown,
          tableData: action.data.tableData,
          isTableLoading: false,
        }
      }
    default:
      return state
  }
}


const DashboardContext = createContext(null);
const DashboardDispatchContext = createContext(null);

export const useDashboardContext = () => {
  const context = useContext<DashboardState>(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboardContext must be used within a DashboardContextProvider');
  }
  return context;
}

export const useDashboardDispatch = () => {
  const context = useContext<Dispatch<DashboardAction>>(DashboardDispatchContext);
  if (context === undefined) {
    throw new Error('useDashboardDispatch must be used within a DashboardContextProvider');
  }
  return context;
}

export const DashboardContextProvider = ({ children }: PropsWithChildren) => {
  const [dashboardState, dispatch] = useReducer(dashboardReducer, initialDashboardState);
  const { seller, user } = useUserContext();
  const [hasLoadedAllDashboardData, setHasLoadedAllDashboardData] = useState<boolean>(false);

  useEffect(() => {
    const fetchSalesSnapshotData = async () => {

      const initialSnapshotIntervals = dashboardState.salesSnapshotCards.map((snapshot) => snapshot.defaultInterval)
      const initialActiveSnapshotCard = initialSnapshotIntervals[dashboardState.activeSnapshotIndex]

      dispatch({ type: 'DASHBOARD_PAGE_VISITED' })

      let salesByInterval: SalesSnapshotResponseBody = [];
      
      try {
        salesByInterval = await getSellerSalesSnapshot(initialSnapshotIntervals);
      } catch (error) {
        logRocketError(`getSellerSalesSnapshot failed - intervals: ${JSON.stringify(initialSnapshotIntervals)} - error: ${error}`);
        // Continue with empty array to prevent crash
      }

      dispatch({ type: 'ALL_SNAPSHOT_DATA_LOADED', data: { salesData: salesByInterval } })

      if (dashboardState.activeBreakdownTab === 'products') {
        const productSalesResponse = await getProductSalesBreakdown(initialActiveSnapshotCard)
        dispatch({ type: 'PRODUCT_SALES_BREAKDOWN_DATA_LOADED', data: { tableData: productSalesResponse } })
      } else if (dashboardState.activeBreakdownTab === 'order-items') {
        const orderItemsalesResponse = await getOrderItemSalesBreakdown(
          initialActiveSnapshotCard)
        dispatch({ type: 'ORDER_ITEM_SALES_BREAKDOWN_DATA_LOADED', data: { tableData: orderItemsalesResponse } })
      } else {
        const salesBreakdown = await getSellerSalesBreakdown(initialActiveSnapshotCard)
        dispatch({ type: 'SALES_BREAKDOWN_DATA_LOADED', data: { breakdownData: salesBreakdown } })
      }

      setHasLoadedAllDashboardData(true);
    }

    //Don't need to fetch dashboard data if users role is Admin
    if (user?.role !== UserRole.ADMIN && user?.role !== UserRole.SUPPORT) {
      if (seller && !hasLoadedAllDashboardData && !!seller.sellerId) {
        fetchSalesSnapshotData();
      }
    }
  }, [seller, user])

  return (
    <DashboardContext.Provider value={dashboardState}>
      <DashboardDispatchContext.Provider value={dispatch}>
        {children}
      </DashboardDispatchContext.Provider>
    </DashboardContext.Provider>
  );
}